import React, { useState, useMemo } from 'react';
import SvgaPlayer from './SvgaPlayer';
import constant from '../constants/constant';

export const resolveAssetUrl = (url) => {
  if (!url) return '';
  if (typeof url === 'object') {
    if (url instanceof File || url instanceof Blob) {
      return URL.createObjectURL(url);
    }
    return '';
  }
  if (typeof url !== 'string') return String(url);
  let trimmed = url.trim();

  // If local blob or data URL, return directly
  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    return trimmed;
  }

  // Prevent Mixed-Content: Force https for admin.xoxolite.com
  if (trimmed.startsWith('http://admin.xoxolite.com')) {
    trimmed = trimmed.replace('http://', 'https://');
  }

  // Fix path for admin.xoxolite.com where backend static files are served at /xoxo/uploads
  if (trimmed.includes('admin.xoxolite.com/uploads/') && !trimmed.includes('/xoxo/uploads/')) {
    trimmed = trimmed.replace('admin.xoxolite.com/uploads/', 'admin.xoxolite.com/xoxo/uploads/');
  }

  // If already absolute http/https
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Relative path resolution
  const baseOrigin = constant.API_BASE_URL ? constant.API_BASE_URL.replace(/\/api$/, '') : 'https://admin.xoxolite.com/xoxo';
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${baseOrigin}${cleanPath}`;
};

export const isSvgaAsset = (url) => {
  if (!url) return false;
  if (typeof url === 'object') {
    return !!(url.name && url.name.toLowerCase().endsWith('.svga'));
  }
  if (typeof url !== 'string') return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return clean.endsWith('.svga');
};

export const isSvgAsset = (url) => {
  if (!url) return false;
  if (typeof url === 'object') {
    if (url.name && url.name.toLowerCase().endsWith('.svg')) return true;
    if (url.type && url.type.includes('svg')) return true;
    return false;
  }
  if (typeof url !== 'string') return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return clean.endsWith('.svg') || url.startsWith('data:image/svg+xml');
};

export const isVideoAsset = (url) => {
  if (!url) return false;
  if (typeof url === 'object') {
    if (url.type && url.type.startsWith('video/')) return true;
    if (url.name) {
      const clean = url.name.toLowerCase();
      return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov');
    }
    return false;
  }
  if (typeof url !== 'string') return false;
  const clean = url.split('?')[0].split('#')[0].toLowerCase();
  return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov');
};

const AssetPreview = ({
  src,
  alt = 'Asset Preview',
  className = 'w-full h-full object-contain',
  fallbackPlaceholder = 'https://via.placeholder.com/150?text=No+Asset'
}) => {
  const [imgError, setImgError] = useState(false);
  const [triedLiveFallback, setTriedLiveFallback] = useState(false);

  if (!src) {
    return (
      <img
        src={fallbackPlaceholder}
        alt={alt}
        className={className}
      />
    );
  }

  // 1. SVGA Animation
  if (isSvgaAsset(src)) {
    return <SvgaPlayer src={src} className={className} />;
  }

  // 2. SVG Vector Graphic
  if (isSvgAsset(src)) {
    return <SvgaPlayer src={src} isSvgExplicit={true} className={className} />;
  }

  const resolved = resolveAssetUrl(src);

  // Check again after resolving URL string
  if (isSvgaAsset(resolved)) {
    return <SvgaPlayer src={resolved} className={className} />;
  }

  if (isSvgAsset(resolved)) {
    return <SvgaPlayer src={resolved} isSvgExplicit={true} className={className} />;
  }

  // 3. Video Player
  if (isVideoAsset(src) || isVideoAsset(resolved)) {
    return (
      <video
        src={resolved}
        className={className}
        autoPlay
        loop
        muted
        playsInline
      />
    );
  }

  const handleImgError = () => {
    // If local fetch failed on localhost, attempt live server URL
    if (!triedLiveFallback && typeof src === 'string' && (src.startsWith('/uploads') || src.startsWith('uploads'))) {
      setTriedLiveFallback(true);
      return;
    }
    setImgError(true);
  };

  const currentImgSrc = triedLiveFallback
    ? `https://admin.xoxolite.com/xoxo/${src.replace(/^\/+/, '')}`
    : (imgError ? fallbackPlaceholder : resolved);

  // 4. Standard Raster Image
  return (
    <img
      src={currentImgSrc}
      alt={alt}
      className={className}
      onError={handleImgError}
      loading="lazy"
    />
  );
};

export default AssetPreview;
